import { useState, useDeferredValue } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { contactsApi, companiesApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";
import { detailText } from "../utils/apiErrors";

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  role: "",
  notes: "",
  company_id: "",
  is_primary: false,
};

export default function ContactsPage() {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");

  const deferredSearch = useDeferredValue(search);

  const { data: contacts = [], isLoading, isError: contactsFailed } = useQuery({
    queryKey: ["contacts", deferredSearch, companyFilter],
    queryFn: () =>
      contactsApi.list({
        ...(deferredSearch ? { search: deferredSearch } : {}),
        ...(companyFilter ? { company_id: companyFilter } : {}),
      }),
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: contactsApi.create,
    onSuccess: () => {
      addNotification("איש קשר נוצר בהצלחה", "success");
      qc.invalidateQueries({ queryKey: ["contacts"] });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => contactsApi.update(id, data),
    onSuccess: () => {
      addNotification("איש הקשר עודכן", "success");
      qc.invalidateQueries({ queryKey: ["contacts"] });
      setEditing(null);
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: contactsApi.delete,
    onSuccess: () => {
      addNotification("איש הקשר נמחק", "info");
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
    // Deleting is admin-only server-side, and the button is shown to everyone: without this a
    // technician confirmed the dialog, got a 403 and saw nothing happen.
    onError: (err) => addNotification(detailText(err?.response?.data?.detail, "מחיקת איש הקשר נכשלה"), "error"),
  });

  const handleCreate = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.company_id) { delete payload.company_id; }
    createMutation.mutate(payload);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    const { id, ...data } = editing;
    // Explicit null, not an omitted key: ContactUpdate applies exclude_unset, so omitting
    // company_id here would leave an existing company untouched even though the user blanked
    // the select expecting it to clear -- company_id is not in ContactUpdate.non_nullable, so
    // an explicit null is exactly how this schema already supports clearing it.
    updateMutation.mutate({ id, data: { ...data, company_id: data.company_id || null } });
  };

  const openEdit = (c) => {
    setEditing({
      id: c.id,
      first_name: c.first_name,
      last_name: c.last_name ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      role: c.role ?? "",
      notes: c.notes ?? "",
      company_id: c.company_id ?? "",
      is_primary: c.is_primary,
    });
    setShowForm(false);
  };

  const closeForm = () => {
    createMutation.reset();          // a failed save's message must not greet the next form (another contact / "new")
    updateMutation.reset();
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const isFormOpen = showForm || !!editing;
  const activeForm = editing ?? form;
  const setActiveForm = editing ? setEditing : setForm;
  const mutationPending = editing ? updateMutation.isPending : createMutation.isPending;
  const mutationError = editing ? updateMutation.error : createMutation.error;

  const fullName = (c) =>
    [c.first_name, c.last_name].filter(Boolean).join(" ");

  return (
    <div className="space-y-5 max-w-6xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">אנשי קשר</h1>
        <button
          onClick={() => { setEditing(null); setShowForm((s) => !s); }}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + איש קשר חדש
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="חיפוש לפי שם / אימייל / טלפון"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm w-72"
        />
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">כל החברות</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Create / Edit form */}
      {isFormOpen && (
        <form
          onSubmit={editing ? handleUpdate : handleCreate}
          className="bg-white rounded-xl shadow-sm border p-6 space-y-4"
        >
          <h2 className="font-semibold text-gray-700">
            {editing ? "עריכת איש קשר" : "הוספת איש קשר"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contact-first-name" className="block text-xs text-gray-500 mb-1">שם פרטי *</label>
              <input
                id="contact-first-name"
                required
                value={activeForm.first_name}
                onChange={(e) => setActiveForm((f) => ({ ...f, first_name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="contact-last-name" className="block text-xs text-gray-500 mb-1">שם משפחה</label>
              <input
                id="contact-last-name"
                value={activeForm.last_name}
                onChange={(e) => setActiveForm((f) => ({ ...f, last_name: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs text-gray-500 mb-1">אימייל</label>
              <input
                id="contact-email"
                type="email"
                value={activeForm.email}
                onChange={(e) => setActiveForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="contact-phone" className="block text-xs text-gray-500 mb-1">טלפון</label>
              <input
                id="contact-phone"
                value={activeForm.phone}
                onChange={(e) => setActiveForm((f) => ({ ...f, phone: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              {/* Not required: a company-less contact is a normal, backend-supported state
                  (ContactCreate/ContactUpdate.company_id is Optional, and TicketDetail.jsx's own
                  "add sender as contact" flow creates them for a company-less ticket) -- a
                  required attribute on this SHARED create/edit select otherwise blocked the
                  browser from ever submitting an edit to an already company-less contact, even
                  for an unrelated field change. handleCreate already stripped an empty value
                  before create; this was previously dead code since `required` never let it
                  reach that point in the first place. */}
              <label htmlFor="contact-company" className="block text-xs text-gray-500 mb-1">חברה</label>
              <select
                id="contact-company"
                value={activeForm.company_id}
                onChange={(e) => setActiveForm((f) => ({ ...f, company_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">בחר חברה</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-role" className="block text-xs text-gray-500 mb-1">תפקיד</label>
              <input
                id="contact-role"
                value={activeForm.role}
                onChange={(e) => setActiveForm((f) => ({ ...f, role: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="contact-notes" className="block text-xs text-gray-500 mb-1">הערות</label>
              <textarea
                id="contact-notes"
                rows={2}
                value={activeForm.notes}
                onChange={(e) => setActiveForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_primary"
                checked={activeForm.is_primary}
                onChange={(e) => setActiveForm((f) => ({ ...f, is_primary: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="is_primary" className="text-sm text-gray-600">איש קשר ראשי</label>
            </div>
          </div>

          {mutationError && (
            <p className="text-red-600 text-sm">{detailText(mutationError?.response?.data?.detail, "שמירת איש הקשר נכשלה")}</p>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={mutationPending}
              className="bg-brand-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {mutationPending ? "שומר..." : editing ? "שמור שינויים" : "הוסף איש קשר"}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : contactsFailed && contacts.length === 0 ? (
        <div role="alert" className="text-center py-16 text-red-600">
          <p className="font-medium">לא ניתן לטעון את אנשי הקשר</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          {search || companyFilter ? "לא נמצאו אנשי קשר התואמים את החיפוש" : "לא קיימים אנשי קשר במערכת"}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-right px-4 py-3">שם</th>
                <th className="text-right px-4 py-3">אימייל</th>
                <th className="text-right px-4 py-3">חברה</th>
                <th className="text-right px-4 py-3">טלפון</th>
                <th className="text-right px-4 py-3">תפקיד</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {fullName(c)}
                    {c.is_primary && (
                      <span className="mr-2 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">ראשי</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.email ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{c.company_name ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600"><span dir="ltr">{c.phone ?? "—"}</span></td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{c.role ?? "—"}</td>
                  <td className="px-4 py-3 text-left">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => openEdit(c)}
                        className="text-xs text-brand-600 hover:underline"
                      >
                        עריכה
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`למחוק את ${fullName(c)}?`)) {
                            deleteMutation.mutate(c.id);
                          }
                        }}
                        disabled={deleteMutation.isPending && deleteMutation.variables === c.id}
                        className="text-xs text-red-500 hover:underline disabled:opacity-50"
                      >
                        מחיקה
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
